import { useCallback, useEffect, useState } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './Payroll.css';

function MyPayslip() {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('payslip');
    const [employeePayslip, setEmployeePayslip] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [salaryRules, setSalaryRules] = useState([]);

    // Bank Details State
    const [bankDetails, setBankDetails] = useState(null);
    const [bankFormData, setBankFormData] = useState({
        bankName: '',
        accountNumber: '',
        ifscCode: '',
        accountHolderName: '',
        panNumber: '',
        upiId: ''
    });
    const [qrCodeFile, setQrCodeFile] = useState(null);
    const [qrCodePreview, setQrCodePreview] = useState(null);
    const [savingBank, setSavingBank] = useState(false);
    const [showBankModal, setShowBankModal] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    // Fetch salary rules dynamically
    const fetchRules = useCallback(async () => {
        try {
            const response = await api.get('/salary-rules');
            if (response.data) {
                setSalaryRules(response.data);
            }
        } catch (error) {
            console.error('Error fetching rules:', error);
        }
    }, []);

    // Fetch employee's bank details
    const fetchBankDetails = useCallback(async () => {
        try {
            const response = await api.get('/users/my-bank-details');
            if (response.data) {
                setBankDetails(response.data);
                setBankFormData({
                    bankName: response.data.bankName || '',
                    accountNumber: response.data.accountNumber || '',
                    ifscCode: response.data.ifscCode || '',
                    accountHolderName: response.data.accountHolderName || '',
                    panNumber: response.data.panNumber || '',
                    upiId: response.data.upiId || ''
                });
            }
        } catch (error) {
            console.error('Error fetching bank details:', error);
        }
    }, []);

    // Save bank details
    const validateBankForm = () => {
        const errors = {};

        if (!bankFormData.bankName.trim()) {
            errors.bankName = 'Bank name is required';
        }

        if (!bankFormData.accountNumber.trim()) {
            errors.accountNumber = 'Account number is required';
        } else if (!/^\d{9,18}$/.test(bankFormData.accountNumber.trim())) {
            errors.accountNumber = 'Account number must be 9-18 digits';
        }

        if (!bankFormData.ifscCode.trim()) {
            errors.ifscCode = 'IFSC code is required';
        } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bankFormData.ifscCode.trim())) {
            errors.ifscCode = 'Invalid IFSC code format (e.g., SBIN0001234)';
        }

        if (!bankFormData.accountHolderName.trim()) {
            errors.accountHolderName = 'Account holder name is required';
        } else if (bankFormData.accountHolderName.trim().length < 3) {
            errors.accountHolderName = 'Name must be at least 3 characters';
        }

        if (bankFormData.panNumber && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(bankFormData.panNumber.trim())) {
            errors.panNumber = 'Invalid PAN format (e.g., ABCDE1234F)';
        }

        if (bankFormData.upiId && !/^[\w.-]+@[\w]+$/.test(bankFormData.upiId.trim())) {
            errors.upiId = 'Invalid UPI ID format (e.g., name@upi)';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSaveBankDetails = async () => {
        if (!validateBankForm()) {
            return;
        }

        setSavingBank(true);
        try {
            // First save bank details
            await api.put('/users/my-bank-details', bankFormData);

            // Then upload QR code if a new file was selected
            if (qrCodeFile) {
                const formData = new FormData();
                formData.append('qrCode', qrCodeFile);
                await api.post('/users/upload-qr-code', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            alert('✅ Bank details and QR code submitted for HR verification!');
            setShowBankModal(false);
            setQrCodeFile(null);
            setQrCodePreview(null);
            fetchBankDetails();
        } catch (error) {
            console.error('Error saving bank details:', error);
            alert('Error saving bank details. Please try again.');
        } finally {
            setSavingBank(false);
        }
    };

    const openBankModal = () => {
        setFormErrors({});
        setQrCodeFile(null);
        setQrCodePreview(null);
        setShowBankModal(true);
    };

    // Handle QR Code Selection (store in state, don't upload yet)
    const handleQRCodeSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            alert('Please upload an image file (JPEG, PNG, GIF, or WebP)');
            e.target.value = '';
            return;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            alert('File size must be less than 5MB');
            e.target.value = '';
            return;
        }

        // Store file in state
        setQrCodeFile(file);

        // Create preview URL
        const reader = new FileReader();
        reader.onloadend = () => {
            setQrCodePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    // Remove selected QR code from state
    const handleRemoveQRCodeSelection = () => {
        setQrCodeFile(null);
        setQrCodePreview(null);
    };

    // Upload QR Code Immediately (for Bank Details tab view, not modal)
    const handleQRCodeUploadImmediate = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            alert('Please upload an image file (JPEG, PNG, GIF, or WebP)');
            e.target.value = '';
            return;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            alert('File size must be less than 5MB');
            e.target.value = '';
            return;
        }

        const formData = new FormData();
        formData.append('qrCode', file);

        try {
            setSavingBank(true);
            await api.post('/users/upload-qr-code', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            alert('✅ Payment QR code uploaded successfully!');
            fetchBankDetails();
        } catch (error) {
            console.error('Error uploading QR code:', error);
            alert('Error uploading QR code. Please try again.');
        } finally {
            setSavingBank(false);
        }
    };

    // Delete QR Code
    const handleDeleteQRCode = async () => {
        if (!window.confirm('Are you sure you want to delete your payment QR code?')) return;

        try {
            await api.delete('/users/delete-qr-code');
            alert('QR code deleted successfully!');
            fetchBankDetails();
        } catch (error) {
            console.error('Error deleting QR code:', error);
            alert('Error deleting QR code. Please try again.');
        }
    };

    // Fetch employee's own payslip
    const fetchEmployeePayslip = useCallback(async () => {
        setLoading(true);
        setEmployeePayslip(null); // Clear previous data while fetching
        try {
            const response = await api.get('/payroll/my-payslip', {
                params: {
                    month: selectedMonth,
                    year: selectedYear
                }
            });

            if (response.data) {
                setEmployeePayslip(response.data);
            }
        } catch (error) {
            console.error('Error fetching payslip:', error);
            // No sample data usage - fully dynamic
            setEmployeePayslip(null);
        } finally {
            setLoading(false);
        }
    }, [selectedMonth, selectedYear]);

    useEffect(() => {
        fetchRules();
        fetchEmployeePayslip();
        fetchBankDetails();
    }, [fetchRules, fetchEmployeePayslip, fetchBankDetails]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    // Helper for PDF: simple formatting without special symbols
    const formatCurrencyForPDF = (amount) => {
        return (amount || 0).toLocaleString('en-IN', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2
        });
    };

    const months = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
        { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
        { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
    ];

    const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

    // Download payslip using backend template
    const handleDownloadPayslip = async () => {
        if (!employeePayslip) return;

        try {
            const doc = new jsPDF();
            const monthName = months.find(m => m.value === selectedMonth)?.label;
            const pw = doc.internal.pageSize.getWidth();
            const mg = 14; // margin
            const cw = pw - mg * 2;
            const GREEN = [67, 160, 71];
            const GREEN_LIGHT = [232, 245, 233];
            const GREEN_DARK = [46, 125, 50];
            const YELLOW = [255, 249, 196];
            const BORDER_CLR = [165, 214, 167];
            const tableGap = 5; // gap between left and right tables

            const employee = employeePayslip.employee || {};
            const sd = employeePayslip.salaryDetails || {};
            const deductions = sd.deductions || {};
            const empName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim();

            const fmtAmt = (v) => (Number(v) || 0).toLocaleString('en-IN');

            const numToWords = (num) => {
                if (!num) return 'Zero Rupees Only';
                const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
                    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
                const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
                const cvt = (n) => {
                    if (n < 20) return ones[n];
                    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
                    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + cvt(n % 100) : '');
                    if (n < 100000) return cvt(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + cvt(n % 1000) : '');
                    if (n < 10000000) return cvt(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + cvt(n % 100000) : '');
                    return cvt(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + cvt(n % 10000000) : '');
                };
                return cvt(Math.floor(num)) + ' Rupees Only';
            };

            const cell = (x, cy, w, h, text, opts = {}) => {
                if (opts.fill) { doc.setFillColor(...opts.fill); doc.rect(x, cy, w, h, 'F'); }
                doc.setDrawColor(...BORDER_CLR);
                doc.setLineWidth(0.2);
                doc.rect(x, cy, w, h, 'S');
                if (text !== undefined && text !== null) {
                    doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
                    doc.setFontSize(opts.fs || 7);
                    doc.setTextColor(...(opts.tc || [0, 0, 0]));
                    const tx = opts.align === 'right' ? x + w - 3 : opts.align === 'center' ? x + w / 2 : x + 3;
                    doc.text(String(text), tx, cy + h / 2 + 1, { align: opts.align || 'left', maxWidth: w - 6 });
                }
            };

            let y = mg;

            // ═══════════ HEADER ═══════════
            doc.setFillColor(...GREEN_LIGHT);
            doc.setDrawColor(...GREEN);
            doc.setLineWidth(0.5);
            doc.rect(mg, y, cw, 30, 'FD');

            try {
                const logoData = await new Promise((resolve) => {
                    const img = new Image();
                    img.crossOrigin = 'Anonymous';
                    img.onload = () => {
                        const canvas = document.createElement('canvas');
                        canvas.width = img.naturalWidth;
                        canvas.height = img.naturalHeight;
                        canvas.getContext('2d').drawImage(img, 0, 0);
                        resolve(canvas.toDataURL('image/png'));
                    };
                    img.onerror = () => resolve(null);
                    img.src = '/logo.png';
                });
                if (logoData) doc.addImage(logoData, 'PNG', mg + 2, y + 4, 45, 22);
            } catch (e) { /* skip */ }

            // Company info right-aligned
            const rightEdge = mg + cw - 3;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0);
            doc.text('Talent Recruiter PRIVATE LIMITED', rightEdge, y + 8, { align: 'right' });
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(5.5);
            doc.setTextColor(85, 85, 85);
            doc.text('www.TalentRecruiter.in | hr@TalentRecruiter.in', rightEdge, y + 13, { align: 'right' });
            doc.text('GSTIN: 09AAMCB0572D1ZS | CIN: U55499UP2023PTC191784', rightEdge, y + 17.5, { align: 'right' });
            doc.text('A-43, Sector 63, Noida (201301), Uttar Pradesh', rightEdge, y + 22, { align: 'right' });
            y += 36;

            // ═══════════ TITLE ═══════════
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0);
            doc.text(`PAYSLIP FOR THE MONTH OF ${monthName.toUpperCase()} ${selectedYear}`, pw / 2, y + 5, { align: 'center' });
            y += 14;

            // ═══════════ EMPLOYEE DETAILS ═══════════
            doc.setFillColor(...GREEN);
            doc.rect(mg, y, cw, 9, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7);
            doc.setTextColor(255, 255, 255);
            doc.text('EMPLOYEE DETAILS', mg + 5, y + 6);
            y += 9;

            const rh = 9;
            const hw = cw / 2;
            const lbl = hw * 0.40;
            const val = hw * 0.60;

            const joiningDate = employee.joiningDate ? new Date(employee.joiningDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : 'N/A';
            const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();

            const empRows = [
                ['Employee Code :', employee.employeeId || 'N/A', 'Department :', employee.department || 'N/A'],
                ['Employee Name :', empName, 'Designation :', employee.designation || 'N/A'],
                ["Father's Name :", employee.fatherName || 'N/A', 'Date of Joining :', joiningDate],
                ['Pay Period :', `${monthName} ${selectedYear}`, 'Pay Date :', `${lastDay} ${monthName} ${selectedYear}`],
            ];
            empRows.forEach(r => {
                cell(mg, y, lbl, rh, r[0], { fill: GREEN_LIGHT, bold: true, fs: 6 });
                cell(mg + lbl, y, val, rh, r[1], { bold: true, fs: 6.5 });
                cell(mg + hw, y, lbl, rh, r[2], { fill: GREEN_LIGHT, bold: true, fs: 6 });
                cell(mg + hw + lbl, y, val, rh, r[3], { bold: true, fs: 6.5 });
                y += rh;
            });
            y += 6;

            // ═══════════ ATTENDANCE (left) + SALARY (right) WITH GAP ═══════════
            const leftW = (cw - tableGap) * 0.40;
            const rightW = (cw - tableGap) * 0.60;
            const rightX = mg + leftW + tableGap;
            const secY = y;
            const trh = 10;

            // LEFT HEADER
            doc.setFillColor(...GREEN);
            doc.rect(mg, y, leftW, 9, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(6.5);
            doc.setTextColor(255, 255, 255);
            doc.text('ATTENDANCE DETAILS', mg + 4, y + 6);

            // RIGHT HEADER
            doc.setFillColor(...GREEN);
            doc.rect(rightX, y, rightW, 9, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(6.5);
            doc.setTextColor(255, 255, 255);
            doc.text('SALARY DETAILS', rightX + 4, y + 6);
            y += 9;

            // LEFT: attendance rows
            const attLbl = leftW * 0.65;
            const attVal = leftW * 0.35;

            const presentDays = employeePayslip.workingDays?.present || 0;
            const absentDays = employeePayslip.workingDays?.absent || 0;
            const totalDays = employeePayslip.workingDays?.total || 30;
            const weekOff = Math.max(totalDays - presentDays - absentDays, 0);
            const payDays = presentDays + weekOff;

            const attData = [
                ['Present Days', String(presentDays)],
                ['Absent Days', String(absentDays)],
                ['Week Off', String(weekOff)],
                ['Holidays', '0'],
                ['Pay Days', `${payDays} Days`],
            ];
            let attY = y;
            attData.forEach(r => {
                cell(mg, attY, attLbl, trh, r[0], { fs: 7 });
                cell(mg + attLbl, attY, attVal, trh, r[1], { bold: true, fs: 8, align: 'center' });
                attY += trh;
            });
            cell(mg, attY, attLbl, trh, 'Unpaid Days', { fill: YELLOW, fs: 7 });
            cell(mg + attLbl, attY, attVal, trh, absentDays > 0 ? String(absentDays) : '\u2014', { fill: YELLOW, bold: true, fs: 8, align: 'center' });
            attY += trh;

            // RIGHT: salary sub-header + rows
            const salLbl = rightW * 0.65;
            const salVal = rightW * 0.35;

            cell(rightX, y, salLbl, trh, 'Earnings', { fill: GREEN_LIGHT, bold: true, fs: 6.5, tc: GREEN_DARK });
            cell(rightX + salLbl, y, salVal, trh, 'Amount (\u20B9)', { fill: GREEN_LIGHT, bold: true, fs: 6.5, align: 'right', tc: GREEN_DARK });

            const attendanceBonus = sd.bonus || 0;
            const performanceBonus = sd.gratuity || 0;
            const otherDed = (deductions.pf || 0) + (deductions.esi || 0) + (deductions.tax || 0) +
                (deductions.loan || 0) + (deductions.advance || 0) + (deductions.other || 0) +
                (deductions.lossOfPay || 0) + (deductions.lateDeduction || 0) + (deductions.goalDeduction || 0);

            const salData = [
                ['Basic Salary', fmtAmt(sd.basicSalary), false],
                ['Attendance Bonus', fmtAmt(attendanceBonus), false],
                ['Performance Bonus', fmtAmt(performanceBonus), false],
                ['Other Deductions', otherDed > 0 ? fmtAmt(otherDed) : 'NIL', true],
            ];

            let salCY = y + trh;
            salData.forEach(r => {
                cell(rightX, salCY, salLbl, trh, r[0], { fs: 7 });
                cell(rightX + salLbl, salCY, salVal, trh, r[1], {
                    bold: true, fs: 8, align: 'right',
                    tc: r[2] ? (otherDed > 0 ? [220, 38, 38] : GREEN) : [0, 0, 0]
                });
                salCY += trh;
            });
            cell(rightX, salCY, salLbl, trh, 'Net Salary', { fill: GREEN_LIGHT, bold: true, fs: 8, tc: GREEN_DARK });
            cell(rightX + salLbl, salCY, salVal, trh, fmtAmt(sd.netSalary), { fill: GREEN_LIGHT, bold: true, fs: 9, align: 'right', tc: GREEN_DARK });
            salCY += trh;

            y = Math.max(attY, salCY) + 6;

            // ═══════════ NET SALARY IN WORDS ═══════════
            doc.setFillColor(...YELLOW);
            doc.setDrawColor(...BORDER_CLR);
            doc.setLineWidth(0.2);
            doc.rect(mg, y, cw, 10, 'FD');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(6);
            doc.setTextColor(0, 0, 0);
            doc.text('Net Salary (in words): ', mg + 4, y + 6.5);
            doc.setTextColor(198, 40, 40);
            doc.text(numToWords(sd.netSalary || 0), mg + 40, y + 6.5);
            y += 14;

            // ═══════════ NET PAY FOOTER ═══════════
            doc.setFillColor(...GREEN);
            doc.rect(mg, y, cw, 13, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(255, 255, 255);
            doc.text(`NET PAY:  \u20B9 ${fmtAmt(sd.netSalary || 0)}/-`, pw - mg - 4, y + 9, { align: 'right' });
            y += 18;

            // ═══════════ FOOTER ═══════════
            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.3);
            doc.line(mg, y, pw - mg, y);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(5.5);
            doc.setTextColor(150, 150, 150);
            doc.text('This is a computer-generated payslip and does not require a signature.', pw / 2, y + 5, { align: 'center' });
            doc.text('For queries, contact HR Department | hr@TalentRecruiter.in', pw / 2, y + 10, { align: 'center' });

            doc.save(`Payslip_${empName.replace(/\s+/g, '_')}_${monthName}_${selectedYear}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating salary receipt. Please try again.');
        }
    };

    if (loading) {
        return (
            <div className="page-container">
                <div className="payroll-container">
                    <div className="payroll-loading">
                        <p>Loading your payslip...</p>
                    </div>
                </div>
            </div>
        );
    }



    // Helper to find specific rules for display
    const getRule = (type) => salaryRules.find(r => r.ruleType === type);

    const lateRule = getRule('Late Check-in');
    const goalRule = getRule('Incomplete Goals');
    const absentRule = getRule('Absence');
    const leaveRule = getRule('Uninformed Leave');

    return (
        <div className="page-container">
            <div className="payroll-container">
                {/* Employee Header */}
                <div className="payroll-header">
                    <div className="payroll-header-left">
                        <h1>💰 My Payslip</h1>
                        <p>View your salary details and manage bank information</p>
                    </div>

                    {activeTab === 'payslip' && (
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <select
                                className="filter-select"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                style={{
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    border: '1px solid #e2e8f0',
                                    cursor: 'pointer',
                                    outline: 'none',
                                    fontSize: '14px',
                                    backgroundColor: 'white'
                                }}
                            >
                                {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                            <select
                                className="filter-select"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                style={{
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    border: '1px solid #e2e8f0',
                                    cursor: 'pointer',
                                    outline: 'none',
                                    fontSize: '14px',
                                    backgroundColor: 'white'
                                }}
                            >
                                {years.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                    )}
                    <div className="payroll-tabs">
                        <button
                            className={`tab-btn ${activeTab === 'payslip' ? 'active' : ''}`}
                            onClick={() => setActiveTab('payslip')}
                        >
                            📄 Payslip
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'bank' ? 'active' : ''}`}
                            onClick={() => setActiveTab('bank')}
                        >
                            🏦 Bank Details
                        </button>
                    </div>
                </div>

                {/* Bank Details Tab */}
                {activeTab === 'bank' && (
                    <div className="salary-structures-container">
                        {/* Status Banner */}
                        {bankDetails && (
                            <div style={{
                                padding: '15px 20px',
                                borderRadius: '10px',
                                marginBottom: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '15px',
                                background: bankDetails.bankDetailsStatus === 'verified' ? '#dcfce7' :
                                    bankDetails.bankDetailsStatus === 'rejected' ? '#fee2e2' : '#fef3c7'
                            }}>
                                <span style={{ fontSize: '24px' }}>
                                    {bankDetails.bankDetailsStatus === 'verified' ? '✅' :
                                        bankDetails.bankDetailsStatus === 'rejected' ? '❌' : '⏳'}
                                </span>
                                <div>
                                    <strong style={{
                                        color: bankDetails.bankDetailsStatus === 'verified' ? '#166534' :
                                            bankDetails.bankDetailsStatus === 'rejected' ? '#991b1b' : '#92400e'
                                    }}>
                                        {bankDetails.bankDetailsStatus === 'verified' ? 'Bank Details Verified' :
                                            bankDetails.bankDetailsStatus === 'rejected' ? 'Bank Details Rejected' : 'Pending Verification'}
                                    </strong>
                                    {bankDetails.bankDetailsStatus === 'verified' && bankDetails.bankDetailsVerifiedAt && (
                                        <p style={{ margin: '5px 0 0', fontSize: '13px', color: '#64748b' }}>
                                            Verified on {new Date(bankDetails.bankDetailsVerifiedAt).toLocaleDateString('en-IN')}
                                            {bankDetails.bankDetailsVerifiedBy && ` by ${bankDetails.bankDetailsVerifiedBy.firstName} ${bankDetails.bankDetailsVerifiedBy.lastName}`}
                                        </p>
                                    )}
                                    {bankDetails.bankDetailsStatus === 'rejected' && bankDetails.bankDetailsRejectionReason && (
                                        <p style={{ margin: '5px 0 0', fontSize: '13px', color: '#991b1b' }}>
                                            Reason: {bankDetails.bankDetailsRejectionReason}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="payroll-records">
                            <div className="payroll-records-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <h2>🏦 My Bank Details</h2>
                                    <p style={{ color: '#64748b', fontSize: '13px' }}>
                                        Your bank information for salary transfers
                                    </p>
                                </div>
                                <button
                                    onClick={openBankModal}
                                    style={{
                                        padding: '10px 20px',
                                        background: '#3b82f6',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    ✏️ {bankDetails?.bankName ? 'Edit Details' : 'Add Bank Details'}
                                </button>
                            </div>

                            {/* Bank Details Card View */}
                            <div style={{ padding: '20px' }}>
                                {bankDetails?.bankName ? (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                                        {/* Bank Info Card */}
                                        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0' }}>
                                            <h4 style={{ margin: '0 0 15px', color: '#334155', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                🏦 Bank Information
                                            </h4>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span style={{ color: '#64748b' }}>Bank Name</span>
                                                    <strong style={{ color: '#1e293b' }}>{bankDetails.bankName || '-'}</strong>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span style={{ color: '#64748b' }}>Account Number</span>
                                                    <strong style={{ color: '#1e293b' }}>
                                                        {bankDetails.accountNumber ? `****${bankDetails.accountNumber.slice(-4)}` : '-'}
                                                    </strong>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span style={{ color: '#64748b' }}>IFSC Code</span>
                                                    <strong style={{ color: '#1e293b' }}>{bankDetails.ifscCode || '-'}</strong>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span style={{ color: '#64748b' }}>Account Holder</span>
                                                    <strong style={{ color: '#1e293b' }}>{bankDetails.accountHolderName || '-'}</strong>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Additional Info Card */}
                                        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0' }}>
                                            <h4 style={{ margin: '0 0 15px', color: '#334155', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                📋 Additional Information
                                            </h4>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span style={{ color: '#64748b' }}>PAN Number</span>
                                                    <strong style={{ color: '#1e293b' }}>{bankDetails.panNumber || 'Not provided'}</strong>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span style={{ color: '#64748b' }}>UPI ID</span>
                                                    <strong style={{ color: '#1e293b' }}>{bankDetails.upiId || 'Not provided'}</strong>
                                                </div>
                                            </div>
                                        </div>

                                        {/* QR Code Card */}
                                        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0' }}>
                                            <h4 style={{ margin: '0 0 15px', color: '#334155', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                📱 Payment QR Code
                                            </h4>
                                            {bankDetails?.paymentQRCode ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                    <div style={{
                                                        width: '100px',
                                                        height: '100px',
                                                        border: '2px solid #e2e8f0',
                                                        borderRadius: '8px',
                                                        overflow: 'hidden',
                                                        background: 'white'
                                                    }}>
                                                        <img
                                                            src={`${process.env.REACT_APP_API_URL || 'https://hrmsbackend.TalentRecruiter.in'}/${bankDetails.paymentQRCode}`}
                                                            alt="Payment QR Code"
                                                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                            onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8">Error</text></svg>'; }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <p style={{ color: '#10b981', fontWeight: '600', margin: '0 0 10px' }}>✅ Uploaded</p>
                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                            <label style={{
                                                                padding: '6px 12px',
                                                                background: '#3b82f6',
                                                                color: 'white',
                                                                borderRadius: '5px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px'
                                                            }}>
                                                                Replace
                                                                <input type="file" accept="image/*" onChange={handleQRCodeUploadImmediate} style={{ display: 'none' }} />
                                                            </label>
                                                            <button onClick={handleDeleteQRCode} style={{
                                                                padding: '6px 12px',
                                                                background: '#ef4444',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '5px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px'
                                                            }}>
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ textAlign: 'center', padding: '15px' }}>
                                                    <p style={{ color: '#94a3b8', marginBottom: '10px' }}>No QR code uploaded</p>
                                                    <label style={{
                                                        padding: '8px 16px',
                                                        background: '#8b5cf6',
                                                        color: 'white',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontSize: '13px',
                                                        display: 'inline-block'
                                                    }}>
                                                        📤 Upload QR
                                                        <input type="file" accept="image/*" onChange={handleQRCodeUploadImmediate} style={{ display: 'none' }} />
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '60px 20px', background: '#f8fafc', borderRadius: '12px' }}>
                                        <div style={{ fontSize: '64px', marginBottom: '20px' }}>🏦</div>
                                        <h3 style={{ color: '#334155', marginBottom: '10px' }}>No Bank Details Added</h3>
                                        <p style={{ color: '#64748b', marginBottom: '20px' }}>
                                            Add your bank details for salary transfers
                                        </p>
                                        <button
                                            onClick={openBankModal}
                                            style={{
                                                padding: '12px 24px',
                                                background: '#3b82f6',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontWeight: '600',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            ➕ Add Bank Details
                                        </button>
                                    </div>
                                )}

                                <div style={{ marginTop: '20px', padding: '15px', background: '#f1f5f9', borderRadius: '8px', fontSize: '13px', color: '#64748b' }}>
                                    <strong>⚠️ Important:</strong>
                                    <ul style={{ margin: '10px 0 0', paddingLeft: '20px' }}>
                                        <li>Ensure all details match your bank records exactly</li>
                                        <li>Incorrect details may delay salary transfer</li>
                                        <li>Changes require HR verification before salary processing</li>
                                        <li>Contact HR if you face any issues</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Bank Details Modal */}
                {showBankModal && (
                    <div className="modal-overlay" onClick={() => setShowBankModal(false)}>
                        <div className="modal-container" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>🏦 {bankDetails?.bankName ? 'Edit' : 'Add'} Bank Details</h2>
                                <button className="modal-close" onClick={() => setShowBankModal(false)}>×</button>
                            </div>

                            <div className="modal-body">
                                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '15px' }}>
                                    <div className="form-group">
                                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155' }}>
                                            Bank Name <span style={{ color: '#ef4444' }}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="e.g., State Bank of India"
                                            value={bankFormData.bankName}
                                            onChange={(e) => setBankFormData({ ...bankFormData, bankName: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: '6px',
                                                border: formErrors.bankName ? '2px solid #ef4444' : '1px solid #e2e8f0',
                                                fontSize: '14px'
                                            }}
                                        />
                                        {formErrors.bankName && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{formErrors.bankName}</span>}
                                    </div>
                                    <div className="form-group">
                                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155' }}>
                                            Account Number <span style={{ color: '#ef4444' }}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Enter account number (9-18 digits)"
                                            value={bankFormData.accountNumber}
                                            onChange={(e) => setBankFormData({ ...bankFormData, accountNumber: e.target.value.replace(/\D/g, '') })}
                                            maxLength={18}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: '6px',
                                                border: formErrors.accountNumber ? '2px solid #ef4444' : '1px solid #e2e8f0',
                                                fontSize: '14px'
                                            }}
                                        />
                                        {formErrors.accountNumber && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{formErrors.accountNumber}</span>}
                                    </div>
                                </div>

                                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '15px' }}>
                                    <div className="form-group">
                                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155' }}>
                                            IFSC Code <span style={{ color: '#ef4444' }}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="e.g., SBIN0001234"
                                            value={bankFormData.ifscCode}
                                            onChange={(e) => setBankFormData({ ...bankFormData, ifscCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                                            maxLength={11}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: '6px',
                                                border: formErrors.ifscCode ? '2px solid #ef4444' : '1px solid #e2e8f0',
                                                fontSize: '14px'
                                            }}
                                        />
                                        {formErrors.ifscCode && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{formErrors.ifscCode}</span>}
                                    </div>
                                    <div className="form-group">
                                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155' }}>
                                            Account Holder Name <span style={{ color: '#ef4444' }}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Name as per bank records"
                                            value={bankFormData.accountHolderName}
                                            onChange={(e) => setBankFormData({ ...bankFormData, accountHolderName: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: '6px',
                                                border: formErrors.accountHolderName ? '2px solid #ef4444' : '1px solid #e2e8f0',
                                                fontSize: '14px'
                                            }}
                                        />
                                        {formErrors.accountHolderName && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{formErrors.accountHolderName}</span>}
                                    </div>
                                </div>

                                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '15px' }}>
                                    <div className="form-group">
                                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155' }}>
                                            PAN Number <span style={{ color: '#94a3b8', fontWeight: '400', fontSize: '12px' }}>(Optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="e.g., ABCDE1234F"
                                            value={bankFormData.panNumber}
                                            onChange={(e) => setBankFormData({ ...bankFormData, panNumber: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                                            maxLength={10}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: '6px',
                                                border: formErrors.panNumber ? '2px solid #ef4444' : '1px solid #e2e8f0',
                                                fontSize: '14px'
                                            }}
                                        />
                                        {formErrors.panNumber && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{formErrors.panNumber}</span>}
                                    </div>
                                    <div className="form-group">
                                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155' }}>
                                            UPI ID <span style={{ color: '#94a3b8', fontWeight: '400', fontSize: '12px' }}>(Optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="e.g., yourname@upi"
                                            value={bankFormData.upiId}
                                            onChange={(e) => setBankFormData({ ...bankFormData, upiId: e.target.value.toLowerCase() })}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: '6px',
                                                border: formErrors.upiId ? '2px solid #ef4444' : '1px solid #e2e8f0',
                                                fontSize: '14px'
                                            }}
                                        />
                                        {formErrors.upiId && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{formErrors.upiId}</span>}
                                    </div>
                                </div>

                                {/* QR Code Upload Section */}
                                <div style={{
                                    marginBottom: '15px',
                                    padding: '15px',
                                    background: '#f8fafc',
                                    borderRadius: '10px',
                                    border: '2px dashed #cbd5e1'
                                }}>
                                    <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', color: '#334155' }}>
                                        📱 Payment QR Code <span style={{ color: '#94a3b8', fontWeight: '400', fontSize: '12px' }}>(Optional)</span>
                                    </label>

                                    {qrCodePreview || bankDetails?.paymentQRCode ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                            <div style={{
                                                width: '80px',
                                                height: '80px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                background: 'white'
                                            }}>
                                                <img
                                                    src={qrCodePreview || `${process.env.REACT_APP_API_URL || 'https://hrmsbackend.TalentRecruiter.in'}/${bankDetails.paymentQRCode}`}
                                                    alt="QR Code"
                                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                    onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="10">Error</text></svg>'; }}
                                                />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <p style={{ color: qrCodePreview ? '#f59e0b' : '#10b981', fontWeight: '600', margin: '0 0 8px', fontSize: '13px' }}>
                                                    {qrCodePreview ? '📋 New QR Code Selected (will upload on submit)' : '✅ QR Code Uploaded'}
                                                </p>
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    <label style={{
                                                        padding: '6px 12px',
                                                        background: '#3b82f6',
                                                        color: 'white',
                                                        borderRadius: '5px',
                                                        cursor: 'pointer',
                                                        fontSize: '12px',
                                                        fontWeight: '500'
                                                    }}>
                                                        📤 {qrCodePreview ? 'Change' : 'Replace'}
                                                        <input type="file" accept="image/*" onChange={handleQRCodeSelect} style={{ display: 'none' }} />
                                                    </label>
                                                    {qrCodePreview ? (
                                                        <button onClick={handleRemoveQRCodeSelection} style={{
                                                            padding: '6px 12px',
                                                            background: '#ef4444',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '5px',
                                                            cursor: 'pointer',
                                                            fontSize: '12px',
                                                            fontWeight: '500'
                                                        }}>
                                                            ❌ Remove
                                                        </button>
                                                    ) : (
                                                        <button onClick={handleDeleteQRCode} style={{
                                                            padding: '6px 12px',
                                                            background: '#ef4444',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '5px',
                                                            cursor: 'pointer',
                                                            fontSize: '12px',
                                                            fontWeight: '500'
                                                        }}>
                                                            🗑️ Delete
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                            <div style={{
                                                width: '80px',
                                                height: '80px',
                                                border: '2px dashed #cbd5e1',
                                                borderRadius: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: 'white'
                                            }}>
                                                <span style={{ fontSize: '32px', color: '#cbd5e1' }}>📷</span>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <p style={{ color: '#64748b', margin: '0 0 8px', fontSize: '13px' }}>Upload UPI QR code for easy salary transfers</p>
                                                <label style={{
                                                    padding: '8px 16px',
                                                    background: '#8b5cf6',
                                                    color: 'white',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    fontWeight: '500',
                                                    display: 'inline-block'
                                                }}>
                                                    📤 Select QR Code
                                                    <input type="file" accept="image/*" onChange={handleQRCodeSelect} style={{ display: 'none' }} />
                                                </label>
                                                <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px', marginBottom: 0 }}>
                                                    JPG, PNG, GIF, WebP (Max 5MB)
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div style={{ padding: '12px', background: '#fef3c7', borderRadius: '8px', fontSize: '13px', color: '#92400e' }}>
                                    <strong>Note:</strong> Fields marked with <span style={{ color: '#ef4444' }}>*</span> are required. Changes will require HR verification.
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    className="modal-btn modal-btn-secondary"
                                    onClick={() => setShowBankModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="modal-btn modal-btn-primary"
                                    onClick={handleSaveBankDetails}
                                    disabled={savingBank}
                                    style={{ background: savingBank ? '#94a3b8' : '#10b981' }}
                                >
                                    {savingBank ? '⏳ Saving...' : '💾 Submit for Verification'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Payslip Tab */}
                {activeTab === 'payslip' && !employeePayslip && (
                    <>

                        <div className="payroll-empty">
                            <h3>No Payslip Found</h3>
                            <p>Payroll has not been generated for {months.find(m => m.value === selectedMonth)?.label} {selectedYear} yet.</p>
                        </div>
                    </>
                )}

                {activeTab === 'payslip' && employeePayslip && (
                    <>
                        {/* Month/Year Filter */}


                        {/* Salary Calculation Rules Card - Dynamic */}
                        <div className="salary-rules-card">
                            <h3>📋 Salary Calculation Rules (Active)</h3>
                            <div className="rules-grid">
                                {lateRule && (
                                    <div className="rule-item">
                                        <span className="rule-icon">⏰</span>
                                        <div>
                                            <strong>Late Check-in</strong>
                                            <p>Threshold: {lateRule.threshold} ({lateRule.penaltyType})</p>
                                        </div>
                                    </div>
                                )}
                                {goalRule && (
                                    <div className="rule-item">
                                        <span className="rule-icon">🎯</span>
                                        <div>
                                            <strong>Incomplete Goals</strong>
                                            <p>{goalRule.penaltyType} Penalty</p>
                                        </div>
                                    </div>
                                )}
                                {/* Fallback if no rules loaded yet */}
                                {!lateRule && !goalRule && <p>Loading rules...</p>}
                            </div>
                        </div>

                        {/* Employee Stats */}
                        <div className="payroll-stats employee-stats">
                            <div className="stat-card-payroll blue">
                                <h3>{formatCurrency(employeePayslip.salaryDetails?.grossSalary)}</h3>
                                <p className="stat-label">Gross Salary</p>
                            </div>
                            <div className="stat-card-payroll red">
                                <h3>{formatCurrency(employeePayslip.salaryDetails?.totalDeductions)}</h3>
                                <p className="stat-label">Total Deductions</p>
                            </div>
                            <div className="stat-card-payroll green">
                                <h3>{formatCurrency(employeePayslip.salaryDetails?.netSalary)}</h3>
                                <p className="stat-label">Net Salary</p>
                            </div>
                        </div>

                        {/* Detailed Breakdown */}
                        <div className="payroll-records">
                            <div className="payroll-records-header">
                                <h2>Salary Breakdown - {months.find(m => m.value === selectedMonth)?.label} {selectedYear}</h2>
                                <button className="action-btn paid" onClick={handleDownloadPayslip}>
                                    📥 Download Payslip
                                </button>
                            </div>

                            <div className="employee-breakdown">
                                {/* Earnings */}
                                <div className="breakdown-section">
                                    <h3>💰 Earnings</h3>
                                    <div className="breakdown-items">
                                        <div className="breakdown-item">
                                            <span>Basic Salary</span>
                                            <strong>{formatCurrency(employeePayslip.salaryDetails?.basicSalary)}</strong>
                                        </div>
                                        <div className="breakdown-item total">
                                            <span>Gross Salary</span>
                                            <strong>{formatCurrency(employeePayslip.salaryDetails?.grossSalary)}</strong>
                                        </div>
                                    </div>
                                </div>

                                {/* Deductions */}
                                <div className="breakdown-section">
                                    <h3>⚠️ Deductions</h3>
                                    <div className="breakdown-items">
                                        {(employeePayslip.salaryDetails?.deductions?.lateDeduction > 0) && (
                                            <div className="breakdown-item deduction">
                                                <span>Late Check-ins ({employeePayslip.attendance?.lateCheckIns || 0} days)</span>
                                                <strong className="red">-{formatCurrency(employeePayslip.salaryDetails.deductions.lateDeduction)}</strong>
                                            </div>
                                        )}
                                        {(employeePayslip.salaryDetails?.deductions?.goalDeduction > 0) && (
                                            <div className="breakdown-item deduction">
                                                <span>Incomplete Goals ({employeePayslip.performance?.incompleteGoals || 0})</span>
                                                <strong className="red">-{formatCurrency(employeePayslip.salaryDetails.deductions.goalDeduction)}</strong>
                                            </div>
                                        )}
                                        {(employeePayslip.salaryDetails?.deductions?.lossOfPay > 0) && (
                                            <div className="breakdown-item deduction">
                                                <span>Absences/LOP ({employeePayslip.workingDays?.absent || 0} days)</span>
                                                <strong className="red">-{formatCurrency(employeePayslip.salaryDetails.deductions.lossOfPay)}</strong>
                                            </div>
                                        )}
                                        {(employeePayslip.salaryDetails?.deductions?.pf > 0) && (
                                            <div className="breakdown-item deduction">
                                                <span>Provident Fund (PF)</span>
                                                <strong className="red">-{formatCurrency(employeePayslip.salaryDetails.deductions.pf)}</strong>
                                            </div>
                                        )}
                                        <div className="breakdown-item total">
                                            <span>Total Deductions</span>
                                            <strong className="red">-{formatCurrency(employeePayslip.salaryDetails?.totalDeductions)}</strong>
                                        </div>
                                    </div>
                                </div>

                                {/* Net Pay */}
                                <div className="breakdown-section net-pay">
                                    <h3>💵 Net Pay</h3>
                                    <div className="breakdown-items">
                                        <div className="breakdown-item final">
                                            <span>Amount to be Credited</span>
                                            <strong className="green">{formatCurrency(employeePayslip.salaryDetails?.netSalary)}</strong>
                                        </div>
                                        {employeePayslip.paidAt && (
                                            <div className="breakdown-item">
                                                <span>Payment Date</span>
                                                <strong>{new Date(employeePayslip.paidAt).toLocaleDateString('en-IN')}</strong>
                                            </div>
                                        )}
                                        <div className="breakdown-item">
                                            <span>Status</span>
                                            {employeePayslip.status === 'paid' ? (
                                                <strong style={{
                                                    color: '#166534',
                                                    background: '#dcfce7',
                                                    padding: '8px 16px',
                                                    borderRadius: '20px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '8px'
                                                }}>
                                                    ✅ Salary Paid
                                                </strong>
                                            ) : employeePayslip.status === 'processed' ? (
                                                <strong style={{
                                                    color: '#854d0e',
                                                    background: '#fef9c3',
                                                    padding: '8px 16px',
                                                    borderRadius: '20px'
                                                }}>
                                                    🔄 Processing
                                                </strong>
                                            ) : (
                                                <strong style={{
                                                    color: '#1e40af',
                                                    background: '#dbeafe',
                                                    padding: '8px 16px',
                                                    borderRadius: '20px'
                                                }}>
                                                    📝 Pending
                                                </strong>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Attendance & Performance Details */}
                                <div className="details-grid">
                                    <div className="detail-card">
                                        <h4>📅 Attendance Summary</h4>
                                        <p><strong>Present:</strong> {employeePayslip.attendance?.present}/{employeePayslip.attendance?.total} days</p>
                                        <p><strong>Late Check-ins:</strong> {employeePayslip.attendance?.lateCheckIns} days</p>
                                        <p><strong>Absences:</strong> {employeePayslip.workingDays?.absent} days</p>
                                    </div>
                                    <div className="detail-card">
                                        <h4>🎯 Performance Summary</h4>
                                        <p className="green"><strong>Completed Goals:</strong> {employeePayslip.performance?.status === 'COMPLETED' ? 'Targets Met' : 'In Progress'}</p>
                                        <p className="red"><strong>Incomplete Goals:</strong> {employeePayslip.performance?.incompleteGoals}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default MyPayslip;
